import React, { useState } from 'react';
import { TextField, Button, Box, Typography, CircularProgress, Snackbar, Alert, useMediaQuery } from '@mui/material';
import QRCode from 'qrcode.react';
import './LoginForm.css';

function LoginForm({ onLogin, onSetupTOTP }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [otpSecret, setOtpSecret] = useState(''); // Add state to store OTP secret
  const [setupOtpInput, setSetupOtpInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const isMobile = useMediaQuery('(max-width:600px)');

  const handleSetupTOTP = async () => {
    setLoading(true);
    try {
      const response = await onSetupTOTP(username, password);
      if (response && response.totp_uri && response.secret) { // Check if secret is in the response
        setTotpUri(response.totp_uri);
        setOtpSecret(response.secret); // Store the secret in state
      } else {
        const errorMessage = response?.error || 'Failed to set up OTP';
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      setSnackbarMessage(error.message || 'An error occurred while setting up OTP');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetupOtp = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://frek.cc/proxy/38.54.86.24:19564/confirm-setup-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, otp: setupOtpInput }),
      });

      const data = await response.json();
      if (response.ok) {
        setSnackbarMessage('OTP setup confirmed successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTotpUri('');
        setOtpSecret(''); // Clear the secret after setup is confirmed
        setSetupOtpInput('');
      } else {
        throw new Error(data.error || 'Failed to confirm OTP setup');
      }
    } catch (error) {
      console.error('Network error:', error);
      setSnackbarMessage(error.message || 'An error occurred while confirming OTP setup');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onLogin(username, password, otpInput);
      setSnackbarMessage('Login successful!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error.message || 'An error occurred while trying to log in');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      <div className="login-noise" aria-hidden="true" />
      <div className="login-shell">
        <div className="login-hero">
          <div className="login-badge">Terminal Access</div>
          <h1 className="login-title">
            Access Console
            <span>Authenticate to continue.</span>
          </h1>
          <div className="login-orbit" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
        <form
          className="login-card"
          onSubmit={handleLoginSubmit}
          data-compact={isMobile ? 'true' : 'false'}
          data-loading={loading ? 'true' : 'false'}
        >
          <div className="login-card-header">
            <Typography className="login-card-title" variant="h4">
              Secure Login
            </Typography>
            <Typography className="login-card-subtitle" variant="body2">
              Enter your credentials to continue.
            </Typography>
          </div>
          <div className="login-fields">
            <TextField
              className="login-field"
              label="user>"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              fullWidth
            />
            <TextField
              className="login-field"
              label="pass>"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              fullWidth
            />
            <TextField
              className="login-field"
              label="otp>"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              variant="outlined"
              fullWidth
            />
          </div>
          <div className="login-actions">
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              className="login-btn login-btn-primary"
            >
              {loading ? <CircularProgress size={24} className="login-spinner" /> : 'Login'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleSetupTOTP}
              disabled={loading}
              className="login-btn login-btn-ghost"
            >
              {loading ? <CircularProgress size={24} className="login-spinner" /> : 'Set Up OTP'}
            </Button>
          </div>

          {totpUri && (
            <Box mt={2} className="login-otp">
              <Typography variant="h6">Scan this QR with Google Authenticator</Typography>
              <div className="login-qr">
                <QRCode value={totpUri} />
              </div>
              <Typography variant="body2" className="login-otp-key">
                Or enter this key: <strong>{otpSecret}</strong>
              </Typography>
              <TextField
                className="login-field"
                label="otp-confirm>"
                value={setupOtpInput}
                onChange={(e) => setSetupOtpInput(e.target.value)}
                variant="outlined"
                fullWidth
                margin="normal"
              />
              <Button
                variant="contained"
                onClick={handleConfirmSetupOtp}
                disabled={loading}
                className="login-btn login-btn-primary"
              >
                {loading ? <CircularProgress size={24} className="login-spinner" /> : 'Confirm OTP Setup'}
              </Button>
            </Box>
          )}
        </form>
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default LoginForm;
